import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useSSEConnected } from '../../hooks/useSSE'
import { useAuth } from '../../context/AuthContext'
import PersonaAvatar from '../../components/PersonaAvatar'
import WelcomeSlideshow from '../../components/WelcomeSlideshow'
import type { Persona } from '../../lib/types'

interface CommunityData {
  community: { name: string; slug: string; settings?: string | Record<string, unknown> }
  threads: { id: string; title: string; message_count?: number; last_message_at?: string; last_message_by?: string }[]
  greeting?: { greeting?: string; subtitle?: string }
  return_greeting?: { greeting?: string; subtitle?: string }
}

export default function CommunityLobbyPage() {
  const { user } = useAuth()
  useSSEConnected()
  const slug = user?.communitySlug || ''
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const [showVideo, setShowVideo] = useState(false)

  const communityId = user?.communityId || `portal-${slug}`
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

  // Fetch community data
  const { data: communityData } = useQuery({
    queryKey: ['communityLobby', slug],
    queryFn: async () => {
      const res = await fetch(`/api/threads?community=${slug}`, { headers })
      if (!res.ok) return null
      return res.json() as Promise<CommunityData>
    },
    enabled: !!slug,
  })

  const { data: personas } = useQuery({
    queryKey: ['communityPersonas', communityId, slug],
    queryFn: async () => {
      const ids = [...new Set([communityId, `portal-${slug}`])]
      for (const pid of ids) {
        try {
          const res = await fetch(`/api/communities/${pid}/personas`, { headers })
          if (!res.ok) continue
          const data = await res.json()
          const names: string[] = data.personas || (Array.isArray(data) ? data : [])
          if (names.length > 0) {
            return names.map((name: string) => ({
              name,
              display_name: name.charAt(0).toUpperCase() + name.slice(1),
            })) as Persona[]
          }
        } catch { /* try next */ }
      }
      return []
    },
    enabled: !!slug,
  })

  // Fetch community objects for music, gallery, curated tracks, and config
  const { data: communityObjects } = useQuery({
    queryKey: ['communityObjects', communityId],
    queryFn: async () => {
      const fetchObjects = async (type: string) => {
        const res = await fetch(`/api/communities/${communityId}/objects?type=${type}`, { headers }).catch(() => null)
        if (!res?.ok) return []
        const data = await res.json()
        const arr = Array.isArray(data) ? data : (data.objects || [])
        return arr.map((o: Record<string, unknown>) => {
          const d = typeof o.data === 'string' ? JSON.parse(o.data as string) : o.data
          return { id: o.id, ...d }
        })
      }
      const [music, gallery, curated, configArr, slideshowArr] = await Promise.all([
        fetchObjects('music_track'),
        fetchObjects('gallery_image'),
        fetchObjects('curated_track'),
        fetchObjects('community_config'),
        fetchObjects('welcome_slideshow'),
      ])
      return { music, gallery, curated, config: configArr[0] || null, slideshow: slideshowArr[0] || null }
    },
    enabled: !!slug,
  })

  const teamPersonas: Persona[] = personas || []

  const config = communityObjects?.config as Record<string, unknown> | null

  const communityName =user?.communityName || communityData?.community?.name || user?.userName || 'there'
  const slideshowConfig = communityObjects?.slideshow as { teamNames: string[]; teamDescs: Record<string, string>; slides: { icon?: string; avatar?: string; avatarBorder?: string; title: string; body: string; narrator: string; showTeam?: boolean; enterBtn?: boolean; audio?: string }[] } | null
  const [showSlideshow, setShowSlideshow] = useState(() => {
    return !localStorage.getItem('community_welcomed_' + slug)
  })
  const heroImage = config?.hero_image as string | undefined
  const mission = config?.mission as { quote?: string; author?: string } | undefined
  const teamNotes = config?.team_notes as { persona: string; quote: string }[] | undefined

  // Override opaque backgrounds when hero is present
  const mainRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (heroImage) {
      const mainEl = mainRef.current?.closest('main')
      const rootEl = document.getElementById('root')
      if (mainEl) mainEl.style.background = 'transparent'
      if (rootEl) rootEl.style.background = 'transparent'
      document.body.style.setProperty('background', 'transparent', 'important')
      document.documentElement.style.background = 'transparent'
      return () => {
        if (mainEl) mainEl.style.background = ''
        if (rootEl) rootEl.style.background = ''
        document.body.style.removeProperty('background')
        document.documentElement.style.background = ''
      }
    }
  }, [heroImage])

  return (
    <div ref={mainRef} className="relative" style={{ background: heroImage ? 'transparent' : undefined }}>
      {/* Welcome Slideshow — first visit only */}
      {showSlideshow && slideshowConfig && (
        <WelcomeSlideshow
          config={slideshowConfig}
          communitySlug={slug}
          communityName={communityName}
          onEnter={() => setShowSlideshow(false)}
        />
      )}

      {/* Hero background */}
      {heroImage && (
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            zIndex: 0,
            backgroundImage: `url(${heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 20%',
          }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to bottom, rgba(10,22,40,0.25) 0%, rgba(10,22,40,0.4) 25%, rgba(10,22,40,0.65) 50%, rgba(10,22,40,0.88) 70%, rgba(10,22,40,1) 90%)',
          }} />
        </div>
      )}

      <div className="relative max-w-5xl mx-auto px-4 py-4" style={{ zIndex: 1 }}>
        {/* Greeting — compact, welcome + open on same line */}
        <div className="mb-6" style={{ paddingTop: heroImage ? 16 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <h1
              className="text-3xl font-bold"
              style={{ color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,1), 0 4px 12px rgba(0,0,0,0.8), 0 0 30px rgba(0,0,0,0.5)', margin: 0 }}
            >
              Welcome, {user?.userName || communityName}.
            </h1>
            <p className="text-xl" style={{ color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,1), 0 4px 12px rgba(0,0,0,0.8)', margin: 0, fontWeight: 600 }}>
              Your studio is{' '}
              <span style={{
                fontFamily: "'Pacifico', cursive",
                fontSize: '1em',
                fontWeight: 700,
                color: '#66ccff',
                textShadow: '0 0 7px #66ccff, 0 0 10px #66ccff, 0 0 21px #66ccff, 0 0 42px #0099dd, 0 0 82px #0099dd',
                animation: 'neonFlicker 2s ease-in-out forwards',
                letterSpacing: 3,
              }}>
                OPEN
              </span>
            </p>
          </div>
          <style>{`
            @keyframes neonFlicker {
              0% { opacity: 0; }
              10% { opacity: 0.8; }
              12% { opacity: 0.2; }
              14% { opacity: 0.9; }
              16% { opacity: 0.4; }
              20% { opacity: 1; }
              22% { opacity: 0.7; }
              25% { opacity: 1; }
              50% { opacity: 0.9; }
              52% { opacity: 1; }
              100% { opacity: 1; }
            }
          `}</style>
        </div>

        {/* Mission Statement + Video Link */}
        {mission?.quote && (
          <div className="rounded-xl p-5 mb-6" style={{ background: '#111D2E', border: '1px solid rgba(212,160,84,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div className="text-sm uppercase tracking-wider mb-2 font-bold" style={{ color: 'var(--accent)', letterSpacing: '2px' }}>Our Mission Together</div>
                <div className="text-lg font-semibold leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                  "{mission.quote}"
                </div>
                {mission.author && (
                  <div className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>— {mission.author}</div>
                )}
              </div>
              {!!config?.welcome_video && (
                <button
                  onClick={() => setShowVideo(true)}
                  className="flex flex-col items-center gap-2 px-5 py-4 rounded-xl transition-all hover:scale-105"
                  style={{ background: '#1A3050', border: '1px solid rgba(212,160,84,0.4)', cursor: 'pointer', flexShrink: 0 }}
                >
                  <span style={{ fontSize: 28 }}>▶</span>
                  <span className="text-sm font-bold" style={{ color: '#FFD700', whiteSpace: 'nowrap', letterSpacing: '0.5px' }}>Watch Welcome Video</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Team Cards — grid with roles */}
        {(() => {
          const teamSource = teamPersonas.length > 0 ? teamPersonas : (teamNotes || []).map(n => ({
            name: n.persona,
            display_name: n.persona.charAt(0).toUpperCase() + n.persona.slice(1),
            role: n.quote,
          }))
          return teamSource.length > 0 ? (
            <section className="mb-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--accent)' }}>
                Your Team
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {teamSource.map((p: any) => (
                  <Link
                    key={p.name}
                    to={`/neighborhood/${p.name}`}
                    className="flex flex-col items-center gap-3 p-5 rounded-xl no-underline transition-all hover:scale-105"
                    style={{ background: 'rgba(17,29,46,0.95)', border: '1px solid rgba(212,160,84,0.15)' }}
                  >
                    <PersonaAvatar name={p.name} size={56} avatarImage={!!p.avatar_image} />
                    <div className="text-center">
                      <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {p.display_name || p.name}
                      </div>
                      {p.role && (() => {
                        const dotIdx = p.role.indexOf('.')
                        const roleTitle = dotIdx > 0 ? p.role.slice(0, dotIdx + 1) : p.role
                        const roleRest = dotIdx > 0 ? p.role.slice(dotIdx + 1) : ''
                        return (
                          <div className="text-xs mt-1.5 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                            <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{roleTitle}</span>
                            {roleRest}
                          </div>
                        )
                      })()}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ) : null
        })()}

      </div>

      {/* Video Modal */}
      {showVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.9)' }}
          onClick={() => setShowVideo(false)}
        >
          <button
            className="absolute top-4 right-4 text-2xl"
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', zIndex: 51 }}
            onClick={() => setShowVideo(false)}
          >
            ✕
          </button>
          <video
            src={String(config?.welcome_video || '')}
            controls
            autoPlay
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}
          />
        </div>
      )}

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={() => setLightboxSrc(null)}
        >
          <button
            className="absolute top-4 right-4 text-2xl"
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
            onClick={() => setLightboxSrc(null)}
          >
            ✕
          </button>
          <img src={lightboxSrc} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
        </div>
      )}
    </div>
  )
}
